import { Component, Vue } from 'vue-property-decorator';
import validator from 'validator';
import { OrganizationTypes, ValidationMasks, ValidationMessages } from '@/enums';
import { SimpleEnumType } from '@/types';

const {
  equals,
  isInt,
  isLength,
  isFloat,
  isURL,
} = validator;

@Component
export default class ValidateMixin extends Vue {
  is_equals = equals;

  // is_url = isURL;

  address = {
    valid: true,
    before_request: '',
    loading: false,
    standardized: true,
  };

  get default_address() {
    return {
      valid: true,
      before_request: '',
      loading: false,
      standardized: true,
    };
  }

  min_length_err = ValidationMessages.min_error;

  get money_settings() {
    return {
      decimal: '.',
      thousands: ' ',
      prefix: '',
      suffix: ' ₽',
      precision: 2,
    };
  }

  get require_message() {
    return ValidationMessages.Messages.REQUIRE;
  }

  get max_file_size_message() {
    return ValidationMessages.Messages.MAX_FILE_SIZE;
  }

  get invalid_file_format_message() {
    return ValidationMessages.Messages.INVALID_FILE_FORMAT;
  }

  get invalid_email() {
    return ValidationMessages.Messages.INVALID_EMAIL;
  }

  get invalid_url() {
    return ValidationMessages.Messages.INVALID_URL;
  }

  get invalid_address() {
    return ValidationMessages.Messages.INVALID_ADDRESS;
  }

  get phone_mask() {
    return ValidationMasks.PHONE;
  }

  get inn_mask_juridic() {
    return ValidationMasks.INN_JURIDIC;
  }

  get inn_mask_base() {
    return ValidationMasks.INN;
  }

  get kpp_mask() {
    return ValidationMasks.KPP;
  }

  get zip_mask() {
    return ValidationMasks.ZIP;
  }

  get invalid_phone_number() {
    return ValidationMessages.Messages.INVALID_PHONE_NUMBERS;
  }

  get invalid_password_format() {
    return ValidationMessages.Messages.INVALID_PASSWORD_FORMAT;
  }

  get invalid_password_matching() {
    return ValidationMessages.Messages.INVALID_PASSWORD_MATCHING;
  }

  get email_exists() {
    return ValidationMessages.Messages.EMAIL_EXISTS;
  }

  get invalid_validation_email_code() {
    return ValidationMessages.Messages.INVALID_VALIDATION_EMAIL_CODE;
  }

  get wrong_old_pass() {
    return ValidationMessages.Messages.WRONG_OLD_PASS;
  }

  get wrong_universal_inn() {
    return ValidationMessages.Messages.WRONG_UNIVERSAL_INN;
  }

  get wrong_org_inn() {
    return ValidationMessages.Messages.WRONG_ORG_INN;
  }

  get wrong_kpp() {
    return ValidationMessages.Messages.WRONG_KPP;
  }

  get wrong_name() {
    return ValidationMessages.Messages.INVALID_NAME_FORMAT;
  }

  get min_length() {
    return ValidationMessages.Messages.MIN_LENGTH;
  }

  get invalid_date() {
    return ValidationMessages.Messages.INVALID_DATE;
  }

  is_by_pattern(value: string, pattern: string): boolean {
    return !!value.match(new RegExp(pattern));
  }

  is_valid_pass(v: string, count = 6): boolean {
    const validate = (pattern: string): boolean => this.is_by_pattern(v, pattern);

    return this.is_length(v, { min: count })
      && validate('[A-Z]')
      && validate('[a-z]')
      && validate('[0-9]')
      && (validate('[!()\'+-/"@#$%^&:;}{<=>?_~|\\[\\]\\\\]'));
  }

  is_length(
    str: string | null,
    options?: { min?: number | undefined, max?: number | undefined },
  ) {
    if (str === null || typeof str === 'undefined') {
      return false;
    }

    return isLength(str, options);
  }

  /**
   *
   * @param val - значение которое проверяем
   * @returns возвращает TRUE когда значение НЕ пустое и FALSE когда наоборот
   */
  is_require(val: unknown): boolean {
    return (val !== undefined && val !== null && val !== '');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  is_required_when(val: any, condition: boolean): boolean {
    return condition ? this.is_require(val) : true;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  is_float(val: any): boolean {
    if (val !== 0) {
      return isFloat(val.toString());
    }

    return true;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  is_int(val: any): boolean {
    return isInt(val.toString());
  }

  is_email(val?: string | null): boolean {
    if (val !== undefined && val !== null) {
      const pattern = /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      // old version
      // const pattern = /^[a-zA-Z{2,}][a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+////

      return pattern.test(val);
      // return isEmail(val);
    }

    return false;
  }

  is_url(val?: string | null): boolean {
    if (val !== undefined && val !== null && val !== '') {
      return isURL(val);
    }

    return true;
  }

  is_correct_name(val?:string | null): boolean {
    if (!val) {
      return true;
    }
    if (val !== undefined && val !== null) {
      const pattern = /^([А-Яа-яё -]{1,23}|[A-Za-z -]{1,23})$/;

      return pattern.test(val.trim());
    }
    return false;
  }

  is_tin(val: string): boolean {
    return isInt(val) && (val.length === 10 || val.length === 12);
  }

  is_time(val: string): boolean {
    return /(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?/.test(val);
  }

  is_correct_phone(v: string) {
    // 18 - с учетом маски, пробелов и +
    if (v !== '') {
      return !!v && v.length === 18;
    }

    return true;
  }

  is_array_filled(v: unknown[] | undefined | null) {
    return Array.isArray(v) ? !!v.length : false;
  }

  is_correct_kpp(v?: string | null) {
    // 9 цифр
    return !!v && v.length === 9;
  }

  is_correct_org_inn(v?: string | null) {
    // 10 цифр
    return !!v && v.length === 10;
  }

  is_correct_inn(v?: string | null) {
    // 12 цифр
    return !!v && v.length === 12;
  }

  /**
   * Получение маски ИНН по типу организации
   * @param { OrganizationTypes | null} type - тип организации
   * @returns { string } маска
   */
  mask_by_org_type(type: OrganizationTypes | null): string {
    return (
      type === OrganizationTypes.ORGANIZATION
        ? ValidationMasks.INN_JURIDIC : ValidationMasks.INN
    );
  }

  /**
   * Получение сообщения об ошибке в ИНН по типу организации
   * @param { OrganizationTypes | null} type - тип организации
   * @returns { string } маска
   */
  valid_inn_msg(type: OrganizationTypes | null): string {
    if (type === OrganizationTypes.ORGANIZATION) {
      return this.wrong_org_inn;
    }

    return this.wrong_universal_inn;
  }

  /**
   * Получение сообщения об ошибке в ИНН по типу организации
   * @param { OrganizationTypes | null} type - тип организации
   * @returns { boolean }
   */
  is_valid_inn(type: SimpleEnumType<OrganizationTypes> | OrganizationTypes | null, val: string | null | undefined): boolean {
    if (type === OrganizationTypes.ORGANIZATION) {
      return this.is_correct_org_inn(val);
    }

    return this.is_correct_inn(val);
  }

  /**
   * Валидация КПП для разных типов организации.
   * для Физ. лиц и ИП "КПП" - необязательное поле,
   * для Юр. лиц - обязательное
   * @param { OrganizationTypes | null} type - тип организации
   * @returns { boolean | string } - true если прошла валидация, string (сообщение об ошибке) если валидация не прошла
   */
  kpp_rules_by_type(type: OrganizationTypes | null, val: string | null): boolean | string {
    if (type === OrganizationTypes.ORGANIZATION) {
      return this.is_require(val) ? (this.is_correct_kpp(val) || this.wrong_kpp) : this.require_message;
    }
    return !val ? true : (this.is_correct_kpp(val) || this.wrong_kpp);
  }

  is_min_length(v?: string | null) {
    // минимум 2 символа
    return !!v && v.length >= 2;
  }

  is_valid_address() {
    return this.address.valid;
  }

  check_address(input: string) {
    return !this.address.standardized && input !== this.address.before_request;
  }

  is_valid_date(v?: string | null) {
    // Регулярка для проверки формата DD.MM.YYYY
    const regex = /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.(\d{4})$/;

    if (!v || !regex.test(v)) {
      return false;
    }

    const [
      day, month, year,
    ] = v.split('.').map(Number);

    // Проверка количества дней в месяце
    const is_leap_year = (year: number) => year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    const days_in_month = (month: number) => [
      31, is_leap_year(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31,
    ][month - 1];

    return day <= days_in_month(month);
  }
}
